import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import Label from '@components/form/Label';
import { Select } from '@components/form/SelectV2';
import Grid from '@components/CSSGrid';
import { Box, Flex } from '@components/layout/Grid';
import EmptyResultsGenre from '@ecosystems/EmptyResultsGenre';
import { videoSettingsModalOpenVar, GET_VIDEO_SETTINGS } from '@lib/apollo';
import VideoCard from '@organisms/cards/VideoCard';
import VideosGenresVideosSkeleton from '@skeletons/VideosGenresVideosSkeleton';
import AnalyticsManager from '@lib/analytics/manager';
import { sectionTitleStyle } from '@components/texts';
import {
  NewTaxonomyItemFieldsFragment,
  useCrossPromotionalBannersQuery,
  useVideosQuery,
  VideoOrderBy,
} from '@gql/generated';
import { Button } from '@components/ButtonV2';
import { PromotionCard } from '@organisms/cross_promotion/Card';

type Props = {
  query: string;
  genre: string;
  taxonomies: NewTaxonomyItemFieldsFragment[];
  selectedTerms: Record<string, string[]>;
  clearAll(): void;
  renderTeachersSection(): React.ReactNode;
};
const VideoGenresList = (props: Props) => {
  const {
    query,
    genre,
    taxonomies,
    selectedTerms,
    clearAll,
    renderTeachersSection,
  } = props;
  const router = useRouter();
  const [orderBy, setOrderBy] = useState(VideoOrderBy['NewestFirst']);
  const [fetchingMore, setFetchingMore] = useState(false);
  const { data: crossPromotionData } = useCrossPromotionalBannersQuery();
  const { data: preferences, loading: loadingPreferences } =
    useQuery(GET_VIDEO_SETTINGS);

  const taxonomiesVariable = Object.keys(selectedTerms).map((key) => ({
    taxonomy: key,
    values: selectedTerms[key],
  }));

  const variables = {
    taxonomies: taxonomiesVariable,
    perPage: 12,
    query: query,
    genre: genre === 'all' ? '' : genre,
    useLocale: false,
    orderBy: orderBy,
    ...Object.assign({}, preferences?.videoSettings),
  };

  const page = parseInt(router.query?.page as string) || 1;

  const getVideosResponse = useVideosQuery({
    variables: Object.assign(variables, preferences?.videoSettings, { page }),
  });

  useDeepCompareEffect(() => {
    if (preferences?.videoSettings) {
      AnalyticsManager().sendSearchEvent({
        taxonomies: variables.taxonomies,
        query: variables.query,
        genre: variables.genre,
      });
    }
  }, [preferences, variables]);

  const openSettingModal = () => {
    videoSettingsModalOpenVar(true);
  };

  const showPromotion = taxonomiesVariable.length === 0 && query === '';
  const cards = useMemo(() => {
    if (getVideosResponse?.data?.videos?.data?.length > 0) {
      return getVideosResponse?.data?.videos?.data
        ?.flatMap?.((video, i, arr) => {
          const videoCard = (
            <VideoCard
              key={video.id}
              className="VideoGenresList--VideoCard"
              video={{
                id: video.id,
                identifier: video.identifier,
                kind: video.kind,
                title: video.content.title,
                cursorAt: video.cursorAt,
                description: video.content?.shortDescription,
                thumbnailUrl: video.assets?.thumbnailUrl,
                flavor: video?.taxonomies?.flavor
                  ? video.taxonomies.flavor.name
                  : '',
                publishedAt: video.meta.publishedAt,
                duration: video.meta.minutes,
                genreName: video.taxonomies?.genre?.name
                  ? video.taxonomies.genre.name
                  : '',
                genreSlug: video.taxonomies?.genre?.slug
                  ? video.taxonomies.genre.slug
                  : '',
                instructors: video.taxonomies?.instructors || [],
                primaryStyle: video.primaryStyle,
              }}
              data-testid="VideoGenresList--VideoCard"
            />
          );

          if (!showPromotion) {
            return [videoCard];
          }

          const promotionIndex = Math.floor(i / 8) - 1;
          const promotion =
            crossPromotionData?.crossPromotionalBanners?.[promotionIndex];

          const promotionCard =
            promotion && i > 0 && i % 8 === 0 ? (
              <PromotionCard
                key={`PromotionCard-${i}`}
                link={promotion.link}
                img={promotion.imageUrl}
              />
            ) : null;

          if (i === 8 && arr.length === 9 && promotionCard) {
            return [promotionCard];
          }

          return [
            i === 8 && promotion ? promotionCard : null,
            videoCard,
            i > 8 && promotion ? promotionCard : null,
          ];
        })
        .filter((item) => !!item);
    }
    return [];
  }, [
    getVideosResponse?.data?.videos?.data,
    getVideosResponse?.data?.videos?.page,
  ]);

  if (loadingPreferences || !taxonomies) return <VideosGenresVideosSkeleton />;

  const { data, loading } = getVideosResponse;
  if (loading && !data) return <VideosGenresVideosSkeleton />;

  const videos = data?.videos;

  if (videos?.totalCount === 0) {
    return (
      <EmptyResultsGenre
        keyword={query}
        emptyTerms={!selectedTerms.length}
        clearAll={clearAll}
        //TODO: FIX THIS
        openSettingModal={openSettingModal}
      />
    );
  }

  let selectedTagsText;
  if (Object.keys(selectedTerms || {}).length > 0) {
    const flattenTags = [];
    taxonomies.forEach((taxonomy) => {
      if (
        selectedTerms.hasOwnProperty(taxonomy.slug)
        // && // we exclude duration from the list
        // taxonomy.slug !== 'durations'
      ) {
        const terms =
          taxonomy.subcategories?.map((sub) => {
            return sub.terms.forEach((term) => {
              if (
                selectedTerms[taxonomy.slug]?.includes?.(term.slug) &&
                !flattenTags.includes(term.name)
              ) {
                flattenTags.push(term.name);
              }
            }, []);
          }) || [];
      }
    }, []);

    let selectedTags = [];
    if (flattenTags.length > 4) {
      selectedTags = flattenTags.slice(0, 4);
    } else {
      selectedTags = [...flattenTags];
    }

    selectedTagsText = selectedTags.join(', ');
    if (flattenTags.length > 4) {
      selectedTagsText = (
        <>
          {selectedTagsText}{' '}
          <Trans
            id="metadata.more_items"
            values={{ number: flattenTags.length - 4 }}
          />
        </>
      );
    }
  }

  return (
    <React.Fragment>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        mb="20px"
        pt={20}
      >
        <Box>
          <Label css={sectionTitleStyle}>
            <Trans
              id="videos_genres.showing_result"
              values={{ x: videos?.totalCount }}
            />
          </Label>
        </Box>
        <div className="flex items-center gap-2">
          <label className="flex-shrink-0 hidden md:block text-dark font-medium text-sm">
            <Trans id="videos_genres.sort_label" />:
          </label>
          <Select
            selectedValue={orderBy}
            options={[
              {
                value: VideoOrderBy['NewestFirst'],
                name: t`sortby.published_at`,
              },
              {
                value: VideoOrderBy['DurationAscending'],
                name: t`sortby.time`,
              },
              {
                value: VideoOrderBy['MostWatched'],
                name: t`sortby.popularity`,
              },
            ]}
            valueKey="value"
            displayTextKey="name"
            onChange={(value) => {
              setOrderBy(value);
            }}
          />
        </div>
      </Flex>
      <Grid cardWidth={320} data-testid="VideoGenresList--VideosContainer">
        {cards.slice(
          0,
          getVideosResponse?.data?.videos?.page *
            getVideosResponse?.data?.videos?.perPage
        )}
      </Grid>
      {cards.length >
        getVideosResponse?.data?.videos?.page *
          getVideosResponse?.data?.videos?.perPage ||
      getVideosResponse?.data?.videos?.totalCount > cards.length ? (
        <Flex pt={30} pb={10} alignItems="center" justifyContent="center">
          <Button
            loading={fetchingMore}
            onClick={async () => {
              setFetchingMore(true);
              await getVideosResponse.fetchMore({
                variables: Object.assign(
                  variables,
                  preferences?.videoSettings,
                  {
                    page: videos.page + 1,
                  }
                ),
              });
              router.push(
                {
                  pathname: router.pathname,
                  query: { ...router.query, page: videos.page + 1 },
                },
                null,
                {
                  shallow: true,
                  scroll: false,
                }
              );
              setFetchingMore(false);
            }}
            data-testid="VideoGenresList--LoadMore"
            outline
          >
            <Trans id="metadata.show_more" message="Show more" />
          </Button>
        </Flex>
      ) : null}
      {renderTeachersSection()}
    </React.Fragment>
  );
};

export default VideoGenresList;
